import { useState, useMemo, useEffect } from "react";
import { Search, User, Scissors } from "lucide-react";
import PageHeader from "../../components/ui/PageHeader";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../hooks/useToast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { format, isValid, parse, startOfDay, endOfDay, startOfMonth, endOfMonth } from "date-fns";
import { getServiceBookings, ServiceBookingItem as ApiServiceBooking } from "../../services/BookingsApi";
import { getBusinessServices } from "../../services/businessServicesApi";
import { ReportDateRangePicker } from "../../components/reports/ReportDateRangePicker";

interface ServiceSaleRow {
  id: number;
  orderNumber: string;
  date: string;
  dateObj: Date | null;
  customerName: string;
  serviceName: string;
  /** Service IDs from items (for filter by service) */
  serviceIds: string[];
  employeeName: string;
  location: string;
  amountToPay: number;
  amountPaid: number;
  paymentMethod: string;
  status: string;
}

const ServiceSalesReportPage = () => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";
  const toast = useToast();

  const [rows, setRows] = useState<ServiceSaleRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>(() => {
    const now = new Date();
    return { start: startOfMonth(now), end: endOfMonth(now) };
  });
  const [statusFilter, setStatusFilter] = useState<string>("completed");
  const [serviceFilter, setServiceFilter] = useState<string>("all");
  const [employeeFilter, setEmployeeFilter] = useState<string>("all");
  const [locationFilter, setLocationFilter] = useState<string>("all");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>("all");

  const [servicesList, setServicesList] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    let cancelled = false;
    const fetchServices = async () => {
      try {
        const list = await getBusinessServices();
        if (cancelled) return;
        setServicesList(list.map((s) => ({ id: s.id, name: s.name })));
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching services:", err);
          toast.error("Failed to load services");
        }
      }
    };
    fetchServices();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await getServiceBookings();
        if (cancelled) return;
        const list = response.data ?? [];
        const mapped: ServiceSaleRow[] = list.map((b: ApiServiceBooking) => {
          let dateObj: Date | null = null;
          try {
            const parsed = parse(b.date_time, "dd MMM yyyy hh:mm a", new Date());
            dateObj = isValid(parsed) ? parsed : null;
          } catch {
            const d = new Date(b.date_time);
            dateObj = !isNaN(d.getTime()) ? d : null;
          }
          let paymentMethod = "Cash";
          if (b.amount_paid > 0 && b.amount_paid < b.amount_to_pay) paymentMethod = "Multiple";
          else if (b.amount_paid >= b.amount_to_pay) paymentMethod = "Card";
          const employeeName = b.employees?.length > 0 ? b.employees[0].name : "No Employee";
          const status = (b.status || "").charAt(0).toUpperCase() + (b.status || "").slice(1);

          const items = b.items ?? [];
          const serviceIds: string[] = [];
          const names: string[] = [];
          for (const item of items) {
            const id = item.business_service_id ?? item.business_service?.id;
            if (id != null) serviceIds.push(String(id));
            const name =
              item.business_service?.name ??
              (item.business_service_id != null
                ? servicesList.find((s) => String(s.id) === String(item.business_service_id))?.name
                : undefined);
            if (name && !names.includes(name)) names.push(name);
          }
          const serviceName = names.length > 0 ? names.join(", ") : "—";

          return {
            id: b.id,
            orderNumber: String(b.id),
            date: b.date_time,
            dateObj,
            customerName: b.name || "—",
            serviceName,
            serviceIds,
            employeeName,
            location: b.location || "",
            amountToPay: Number(b.amount_to_pay) || 0,
            amountPaid: Number(b.amount_paid) || 0,
            paymentMethod,
            status,
          };
        });
        setRows(mapped);
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching service bookings:", err);
          toast.error("Failed to load service sales");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicesList]);

  const uniqueEmployees = useMemo(() => {
    const set = new Set(rows.map((r) => r.employeeName).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);
  const uniqueLocations = useMemo(() => {
    const set = new Set(rows.map((r) => r.location).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);
  const uniquePaymentMethods = useMemo(() => {
    const set = new Set(rows.map((r) => r.paymentMethod).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const matchesSearch =
        !searchTerm ||
        row.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.orderNumber.toLowerCase().includes(searchTerm.toLowerCase());
      let matchesDate = true;
      if (dateRange.start && dateRange.end) {
        if (!row.dateObj) matchesDate = false;
        else
          matchesDate =
            row.dateObj >= startOfDay(dateRange.start) &&
            row.dateObj <= endOfDay(dateRange.end);
      }
      const matchesStatus =
        statusFilter === "all" || row.status.toLowerCase() === statusFilter.toLowerCase();
      const matchesService =
        serviceFilter === "all" || row.serviceIds.includes(serviceFilter);
      const matchesEmployee = employeeFilter === "all" || row.employeeName === employeeFilter;
      const matchesLocation = locationFilter === "all" || row.location === locationFilter;
      const matchesPayment =
        paymentMethodFilter === "all" || row.paymentMethod === paymentMethodFilter;
      return (
        matchesSearch &&
        matchesDate &&
        matchesStatus &&
        matchesService &&
        matchesEmployee &&
        matchesLocation &&
        matchesPayment
      );
    });
  }, [
    rows,
    searchTerm,
    dateRange.start,
    dateRange.end,
    statusFilter,
    serviceFilter,
    servicesList,
    employeeFilter,
    locationFilter,
    paymentMethodFilter,
  ]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRows.slice(start, start + itemsPerPage);
  }, [filteredRows, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredRows.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    dateRange.start,
    dateRange.end,
    statusFilter,
    serviceFilter,
    employeeFilter,
    locationFilter,
    paymentMethodFilter,
  ]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 lg:p-6">
        <div className="flex flex-col w-full h-full">
          <PageHeader
            title="Service Sales List"
            description="View and track all service bookings and appointment sales"
            icon={Scissors}
            variant="card"
            className="mb-0"
          />

          <div className="flex flex-col gap-4 py-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex flex-shrink-0">
                <ReportDateRangePicker
                  startDate={dateRange.start}
                  endDate={dateRange.end}
                  onChange={(start, end) => setDateRange({ start, end })}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px] bg-white border-gray-200">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="booked">Booked</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
              <Select value={serviceFilter} onValueChange={setServiceFilter}>
                <SelectTrigger className="w-[160px] bg-white border-gray-200">
                  <SelectValue placeholder="Service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All services</SelectItem>
                  {servicesList.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                <SelectTrigger className="w-[160px] bg-white border-gray-200">
                  <SelectValue placeholder="Employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All employees</SelectItem>
                  {uniqueEmployees.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger className="w-[160px] bg-white border-gray-200">
                  <SelectValue placeholder="Location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  {uniqueLocations.map((loc) => (
                    <SelectItem key={loc} value={loc}>
                      {loc || "(None)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
                <SelectTrigger className="w-[140px] bg-white border-gray-200">
                  <SelectValue placeholder="Payment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All payment</SelectItem>
                  {uniquePaymentMethods.map((method) => (
                    <SelectItem key={method} value={method}>
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 font-medium whitespace-nowrap">Show</span>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(v) => {
                    setItemsPerPage(Number(v));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[80px] bg-white border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-gray-500 font-medium whitespace-nowrap">per page</span>
              </div>
              <div className="relative w-full lg:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by customer or order..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-[14px] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-transparent transition-all"
                />
              </div>
            </div>
          </div>

          <div className="pb-6">
            <div className="rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-[#885ABB] border-b border-gray-100">
                    <tr className="text-sm font-bold text-white tracking-wide">
                      <th className="px-6 py-4 text-left">Service</th>
                      <th className="px-6 py-4 text-left">Date</th>
                      <th className="px-6 py-4 text-left">Customer</th>
                      <th className="px-6 py-4 text-left">Employee</th>
                      <th className="px-6 py-4 text-left">Location</th>
                      <th className="px-6 py-4 text-right align-middle">Amount to pay</th>
                      <th className="px-6 py-4 text-right align-middle">Amount paid</th>
                      <th className="px-6 py-4 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {isLoading ? (
                      <tr>
                        <td colSpan={8} className="py-20 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent mb-4" />
                            <p className="text-gray-500 font-medium">Loading service sales...</p>
                          </div>
                        </td>
                      </tr>
                    ) : paginatedRows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-20 text-center text-gray-400">
                          <div className="flex flex-col items-center justify-center">
                            <Scissors className="w-12 h-12 mb-3 opacity-20" />
                            <p className="text-lg font-medium text-gray-500">No service sales found</p>
                            <p className="text-sm">Service bookings will appear here.</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedRows.map((row) => (
                        <tr
                          key={row.id}
                          className="hover:bg-purple-50/30 transition-colors group cursor-default"
                        >
                          <td className="px-6 py-4 align-middle min-w-[140px]">
                            <span className="text-sm text-gray-700">{row.serviceName}</span>
                          </td>
                          <td className="px-6 py-4 align-middle">
                            <div className="flex flex-col min-w-[110px]">
                              {row.dateObj ? (
                                <>
                                  <span className="text-sm text-gray-900 font-medium">
                                    {format(row.dateObj, "MMM d, yyyy")}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {format(row.dateObj, "h:mm a")}
                                  </span>
                                </>
                              ) : (
                                <span className="text-sm text-gray-500">{row.date}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-900">
                                {row.customerName}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-700">{row.employeeName}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-700">{row.location || "—"}</span>
                          </td>
                          <td className="px-6 py-4 text-right align-middle">
                            <span className="text-sm font-medium text-gray-700">
                              {currencySymbol}{row.amountToPay.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right align-middle">
                            <span className="text-sm font-black text-gray-900">
                              {currencySymbol}{row.amountPaid.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                row.status.toLowerCase() === "completed"
                                  ? "bg-green-100 text-green-700"
                                  : row.status.toLowerCase() === "refunded"
                                    ? "bg-red-100 text-red-700"
                                    : "bg-yellow-100 text-yellow-700"
                              }`}
                            >
                              {row.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {!isLoading && totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                  <div className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                    {Math.min(currentPage * itemsPerPage, filteredRows.length)} of {filteredRows.length} sales
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      Previous
                    </button>
                    <div className="flex items-center gap-1.5 px-3">
                      <span className="text-sm font-bold text-[#885ABB]">{currentPage}</span>
                      <span className="text-sm text-gray-400">/</span>
                      <span className="text-sm text-gray-500">{totalPages}</span>
                    </div>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceSalesReportPage;
